/**
 * A variant's run settings for runs a person starts and watches — one per
 * kind (#2181 slice 2).
 *
 * **Why a hook.** Four screens need the same answer: the Runs page for its
 * three buttons, the Proposed tab for "Create tests", a single test for
 * "Run this test", and a suite for "Run Suite". Each had been resolving it
 * the same way, and the third would have been the copy that made it a
 * pattern. The variation between them was never in the resolving, only in
 * which kind they wanted.
 *
 * **Why the manual pool specifically.** Every caller here is a person
 * pressing a button and waiting. Asking for the unfiltered list would hand
 * back settings a schedule or a CI rule owns, and using those for a manual
 * run is the collision #2175 removed. The pool argument is required by the
 * API for that reason; this hook is where the callers stop repeating it.
 *
 * **Which one per kind.** The kind's default if it has one, otherwise its
 * first. A variant gets exactly one default per kind when it is created, so
 * the fallback only matters for a variant whose settings were changed by
 * hand.
 */
import { useCallback, useEffect, useState } from 'react'
import { runTemplatesAPI } from '@/lib/api'
import type { RunTemplateKind, UnifiedRunTemplate } from '@/types'

const KINDS: RunTemplateKind[] = ['generation', 'resume', 'replay']

export interface ManualRunTemplates {
  /** The chosen settings per kind. A kind is absent when the variant has
   * none, which callers surface as an unavailable control rather than a
   * control that fails when pressed. */
  byKind: Partial<Record<RunTemplateKind, UnifiedRunTemplate>>
  /** Re-read after something is saved, so a caller showing values does not
   * hold a stale copy. */
  reload: () => Promise<void>
}

export function useManualRunTemplates(variantId: string | null | undefined): ManualRunTemplates {
  const [byKind, setByKind] = useState<Partial<Record<RunTemplateKind, UnifiedRunTemplate>>>({})

  const reload = useCallback(async () => {
    if (!variantId) { setByKind({}); return }
    try {
      const rows = await runTemplatesAPI.listForVariant(variantId, 'manual') as UnifiedRunTemplate[]
      const out: Partial<Record<RunTemplateKind, UnifiedRunTemplate>> = {}
      for (const kind of KINDS) {
        const of = rows.filter(r => r.kind === kind)
        const chosen = of.find(r => r.isDefault) ?? of[0]
        if (chosen) out[kind] = chosen
      }
      setByKind(out)
    } catch {
      // A failed read leaves every kind absent, which reads on screen as
      // "nothing to run with" rather than a control that fails on press.
      setByKind({})
    }
  }, [variantId])

  useEffect(() => { void reload() }, [reload])

  return { byKind, reload }
}
